import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Badge, Button, Card, CardBody, Input, Table } from 'reactstrap'
import Flex from '../../../common/Flex'
import Loader from '../../../common/Loader'
import useSWRFetch from '../../../../helpers/hooks/useSWRFetch'
import { getRewardConfigAudits } from '../../../../helpers/api/rewardsAdmin'
import { formatDate, formatMultiplier } from '../../../rewards/rewardsUtils'
import { RULE_GROUPS, formatDateTime, formatRuleValue, getAuditKeyLabel, getRuleDefinition } from '../rewardsAdminUtils'

const ACTIONS = {
  created: { label: 'Nueva versión', color: 'soft-primary' },
  closed: { label: 'Versión cerrada', color: 'soft-secondary' },
  level_updated: { label: 'Nivel editado', color: 'soft-info' }
}

const PAGE_SIZE = 50

// En la bitácora de niveles los valores son el multiplicador de Coins.
const formatAuditValue = (audit, value) => {
  if (value === null || value === undefined) return '—'
  if (audit.key && audit.key.startsWith('level:')) return formatMultiplier(value)
  return formatRuleValue(audit.key, value)
}

const describeValidity = (audit) => {
  if (!audit.valid_from) return null
  return audit.valid_to
    ? `${formatDate(audit.valid_from)} al ${formatDate(audit.valid_to)}`
    : `Desde el ${formatDate(audit.valid_from)}`
}

// HO-TASK-289: bitácora de cambios de la configuración (GET /rewards/config/audits).
const ConfigAudits = ({ version }) => {
  const [key, setKey] = useState('')
  const [limit, setLimit] = useState(PAGE_SIZE)
  const { data, error, isLoading, mutate } = useSWRFetch(['rewards-config-audits', key, limit, version], (_, auditKey, auditLimit) =>
    getRewardConfigAudits(auditKey || null, auditLimit)
  )
  const audits = data?.data || []

  return (
    <Card className="mb-3">
      <CardBody>
        <Flex justify="between" align="center" className="flex-wrap mb-2">
          <div className="mr-2 mb-2">
            <h5 className="fs-0 mb-1">Bitácora de cambios</h5>
            <p className="fs--1 text-600 mb-0">Quién cambió cada parámetro, cuándo y desde qué fecha rige.</p>
          </div>
          <Input
            type="select"
            bsSize="sm"
            className="mb-2"
            style={{ maxWidth: 260 }}
            value={key}
            onChange={({ target }) => {
              setKey(target.value)
              setLimit(PAGE_SIZE)
            }}
            aria-label="Filtrar la bitácora por parámetro"
          >
            <option value="">Todos los parámetros</option>
            {RULE_GROUPS.map((group) => (
              <optgroup key={group.title} label={group.title}>
                {group.keys.map((ruleKey) => (
                  <option key={ruleKey} value={ruleKey}>{getRuleDefinition(ruleKey).label}</option>
                ))}
              </optgroup>
            ))}
          </Input>
        </Flex>

        {isLoading && <Loader padding="py-4" />}
        {error && (
          <Alert color="danger" className="fs--1 mb-0">
            No pudimos cargar la bitácora.
            <Button color="link" size="sm" className="ml-1 p-0 align-baseline fs--1" onClick={() => mutate()}>
              Reintentar
            </Button>
          </Alert>
        )}
        {data && audits.length === 0 && <p className="fs--1 text-600 mb-0 py-3 text-center">Todavía no hay cambios registrados.</p>}
        {audits.length > 0 && (
          <Table size="sm" responsive className="fs--1 mb-0">
            <thead className="bg-200 text-900">
              <tr>
                <th>Fecha</th>
                <th>Parámetro</th>
                <th>Cambio</th>
                <th>Vigencia</th>
                <th>Motivo</th>
                <th>Usuario</th>
              </tr>
            </thead>
            <tbody>
              {audits.map((audit) => {
                const action = ACTIONS[audit.action] || { label: audit.action, color: 'soft-secondary' }
                const changed = audit.previous_value !== audit.new_value
                return (
                  <tr key={audit.id}>
                    <td className="text-nowrap">{formatDateTime(audit.changed_at)}</td>
                    <td>
                      <div className="text-900">{getAuditKeyLabel(audit.key)}</div>
                      <Badge color={action.color} pill className="fs--2">{action.label}</Badge>
                    </td>
                    <td className="text-nowrap">
                      {changed && audit.previous_value !== null ? (
                        <>
                          {formatAuditValue(audit, audit.previous_value)} → <b>{formatAuditValue(audit, audit.new_value)}</b>
                        </>
                      ) : (
                        <b>{formatAuditValue(audit, audit.new_value)}</b>
                      )}
                    </td>
                    <td className="text-nowrap">{describeValidity(audit) || '—'}</td>
                    <td style={{ minWidth: 160 }}>{audit.note || <span className="text-500">—</span>}</td>
                    <td className="text-nowrap">{audit.changed_by || <span className="text-500">Sistema</span>}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
        {audits.length >= limit && limit < 200 && (
          <div className="text-center mt-2">
            <Button color="link" size="sm" onClick={() => setLimit((current) => Math.min(200, current + PAGE_SIZE))}>
              Ver más cambios
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

ConfigAudits.propTypes = { version: PropTypes.number }

export default ConfigAudits
